// components/Membership.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { initiatePayment } from '../services/api';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const MembershipWrapper = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.primary};
`;

const PageDescription = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.text}aa;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  max-width: 500px;
  margin: 0 auto;
  gap: 1.5rem;
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1.5rem;
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}33;
  }

  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  padding-right: 2.5rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}33;
  }
`;

const Button = styled.button`
  background-color: ${props => props.theme.colors.secondary};
  color: white;
  padding: 1rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    animation: ${props => props.loading ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Message = styled.div`
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
  text-align: center;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  ${props => props.type === 'error' && `
    background-color: #ffe6e6;
    color: #ff0000;
    border: 1px solid #ffcccc;
  `}

  ${props => props.type === 'success' && `
    background-color: #e6ffe6;
    color: #008000;
    border: 1px solid #ccffcc;
  `}
`;

const Benefits = styled.div`
  margin-top: 3rem;
  text-align: center;

  h3 {
    margin-bottom: 1.5rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.li`
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
  }

  svg {
    color: ${props => props.theme.colors.secondary};
    font-size: 1.5rem;
  }
`;

const membershipTypes = {
  regular: {
    name: 'Regular Membership',
    amount: 1000,
  },
  premium: {
    name: 'Premium Membership',
    amount: 2000,
  },
};

const Membership = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    membershipType: 'regular',
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const formattedPhone = formData.phone.replace(/^(?:254|\+254|0)/, '254');
      
      const paymentData = {
        name: formData.name,
        email: formData.email,
        phone: formattedPhone,
        membershipType: formData.membershipType,
        amount: membershipTypes[formData.membershipType].amount
      };

      const response = await initiatePayment(paymentData);

      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Please check your phone to complete the payment',
        });
      } else {
        setMessage({
          type: 'error',
          text: response.message || 'Failed to initiate payment. Please try again.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'An error occurred. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MembershipWrapper>
      <PageTitle>Become a Member</PageTitle>
      <PageDescription>
        Join our community of parents and make a difference in education.
      </PageDescription>

      {message && (
        <Message type={message.type}>
          {message.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          {message.text}
        </Message>
      )}

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Full Name</Label>
          <Input
            id="name"
            type="text"
            name="name"
            placeholder="Enter your full name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            name="phone"
            placeholder="e.g., 0712345678"
            value={formData.phone}
            onChange={handleChange}
            required
            pattern="^(?:254|\+254|0)?([7-9]{1}[0-9]{8})$"
            title="Please enter a valid Kenyan phone number"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="membershipType">Membership Type</Label>
          <Select
            id="membershipType"
            name="membershipType"
            value={formData.membershipType}
            onChange={handleChange}
            required
          >
            <option value="regular">Regular Membership (KES 1,000)</option>
            <option value="premium">Premium Membership (KES 2,000)</option>
          </Select>
        </FormGroup>

        <Button type="submit" disabled={loading}>
          {loading ? (
            <>
              <FaSpinner /> Processing...
            </>
          ) : (
            'Register & Pay Now'
          )}
        </Button>
      </Form>

      <Benefits>
        <h3>Membership Benefits</h3>
        <BenefitsList>
          <BenefitItem>
            <FaCheckCircle />
            <h4>Access to Resources</h4>
            <p>Exclusive access to educational materials and resources</p>
          </BenefitItem>
          <BenefitItem>
            <FaCheckCircle />
            <h4>Monthly Newsletter</h4>
            <p>Stay updated with our monthly newsletter</p>
          </BenefitItem>
          <BenefitItem>
            <FaCheckCircle />
            <h4>Event Participation</h4>
            <p>Priority access to NPA events and workshops</p>
          </BenefitItem>
          {formData.membershipType === 'premium' && (
            <>
              <BenefitItem>
                <FaCheckCircle />
                <h4>One-on-One Support</h4>
                <p>Personal consultation sessions</p>
              </BenefitItem>
              <BenefitItem>
                <FaCheckCircle />
                <h4>Extended Services</h4>
                <p>Additional support and premium features</p>
              </BenefitItem>
            </>
          )}
        </BenefitsList>
      </Benefits>
    </MembershipWrapper>
  );
};

export default Membership;